import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import './css/InventoryPage.css';
import TopNav from '../components/TopNav';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import { EmailSettingsPanel } from './EmailSettingsPage';
import AdminEmployeesPanel from './EmployeesDashboard';
import VendorsPage from './VendorsPage';
import InvoicesPage from './InvoicesPage';
import { ACCESS_PAGES, AUTHORITY_LEVELS, hasPageAuthority } from '../accessControl';

const emptyUser = {
  name: '',
  email: '',
  password: '',
  role: 'employee',
  remoteAccess: false,
  forcePasswordReset: false,
};

const emptyService = {
  name: '',
  description: '',
  defaultPrice: '',
  subServices: '',
};

const emptyDiscount = {
  item: '',
  description: '',
  type: 'Discount',
  price: '',
};

const emptyGroup = {
  name: '',
  description: '',
  serviceIds: [],
  serviceSubServices: {},
};

const emptyAccessGroup = {
  name: '',
  description: '',
  permissions: Object.fromEntries(ACCESS_PAGES.map((page) => [page.key, 'none'])),
  actionPermissions: {},
};
const emptySpecialRule = {
  scopeKey: '',
  label: '',
  description: '',
  fieldKey: 'customer.type',
  operator: 'equals',
  comparisonValue: '',
};
const BUILT_IN_PERMISSION_SCOPES = [
  {
    scopeKey: 'repair_only',
    label: 'Repair only',
    description: 'Limit this permission to repair-order documents.',
  },
];
const emptyPreferences = {
  taxRate: '',
  ipWhitelist: [],
};
const emptyIpForm = {
  ipAddress: '',
  label: '',
};
const passwordPolicyMessage = 'Password must include a capital letter, a number, and a special character.';

const serviceKey = (service) => String(service?.serviceId || service?.id || service?.itemId || service?.name || '');
const discountKey = (discount) => String(discount?.discountId || discount?.id || discount?.item || '');
const groupKey = (group) => String(group?.groupId || group?.id || '');
const validatePasswordPolicy = (password) => (
  /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)
);

const servicePriceType = (service) => (
  String(service?.defaultPriceType || service?.priceType || '').toLowerCase() === 'percent' ? 'percent' : 'amount'
);
const formatPercentValue = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '0';
  return number.toFixed(3).replace(/\.?0+$/, '');
};
const servicePriceText = (service) => {
  const value = service?.defaultPrice ?? service?.price ?? 0;
  return servicePriceType(service) === 'percent'
    ? `${formatPercentValue(value)}%`
    : `$${Number(value).toFixed(2)}`;
};
const parseServicePriceInput = (value, fallbackType = 'amount') => {
  const text = String(value ?? '').trim();
  return {
    defaultPrice: text.replace(/%/g, '').trim(),
    defaultPriceType: text.includes('%') ? 'percent' : fallbackType,
  };
};
const discountPriceType = (discount) => (
  String(discount?.priceType || discount?.price_type || '').toLowerCase() === 'percent' ? 'percent' : 'amount'
);
const parseDiscountPriceInput = (value, fallbackType = 'amount') => {
  const text = String(value ?? '').trim();
  return {
    price: text.replace(/%/g, '').trim(),
    priceType: text.includes('%') ? 'percent' : fallbackType,
  };
};
const serviceInputKey = (service, field) => `${serviceKey(service)}:${field}`;
const discountInputKey = (discount, field) => `${discountKey(discount)}:${field}`;
const serviceSubServiceNames = (service) => {
  const expandDefinition = (value) => {
    const text = String(value || '').trim();
    if (/^\d+$/.test(text)) {
      return Array.from({ length: Math.max(0, Number(text)) }, (_, index) => `Step ${index + 1}`);
    }
    return String(value || '').split(',');
  };
  const source = Array.isArray(service?.subServiceNames)
    ? service.subServiceNames
    : (Array.isArray(service?.sub_service_names)
      ? service.sub_service_names
      : expandDefinition(service?.subServices ?? service?.sub_services ?? ''));
  return [...new Set(source.map((name) => String(name || '').trim()).filter(Boolean))];
};
const selectedSubServicesForService = (service) => (
  Array.isArray(service?.selectedSubServices)
    ? service.selectedSubServices
    : (Array.isArray(service?.selected_sub_services) ? service.selected_sub_services : [])
);
const serviceSubServiceLabel = (name) => String(name || '').trim();
const serviceAssignmentLabel = (service) => {
  const subServices = selectedSubServicesForService(service);
  if (!subServices.length) return service.name;
  return `${service.name}: ${subServices.map(serviceSubServiceLabel).join(', ')}`;
};
const actionPermissionValue = (value) => {
  if (value === true) return 'allowed';
  if (value && typeof value === 'object' && value.granted !== false) {
    const scope = Array.isArray(value.scopes) ? value.scopes[0] : value.scope;
    return scope || 'allowed';
  }
  return 'none';
};

const actionPermissionDisplay = (value, specialRules = []) => {
  const permissionValue = actionPermissionValue(value);
  if (permissionValue === 'none') return 'not allowed';
  if (permissionValue === 'allowed') return 'allowed';
  return [...BUILT_IN_PERMISSION_SCOPES, ...specialRules].find((rule) => (rule.scopeKey || rule.key) === permissionValue)?.label || permissionValue;
};

const actionPermissionPayloadValue = (value) => {
  if (!value || value === 'none') return false;
  if (value === 'allowed') return true;
  return { scope: value };
};

const actionOverrideValue = (value) => {
  if (value === undefined || value === null) return 'inherit';
  if (value === false) return 'deny';
  return actionPermissionValue(value);
};

const actionOverridePayloadValue = (value) => {
  if (!value || value === 'inherit') return undefined;
  if (value === 'deny') return false;
  return actionPermissionPayloadValue(value);
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const canAccess = (pageKey) => hasPageAuthority(user, pageKey, 'read');
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [accessGroups, setAccessGroups] = useState([]);
  const [accessMetadata, setAccessMetadata] = useState({
    actionPermissions: [],
    specialRules: [],
    specialRuleFields: [],
    specialRuleOperators: [],
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEntitlements, setSelectedEntitlements] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedAccessGroupId, setSelectedAccessGroupId] = useState('');
  const [selectedActionOverrides, setSelectedActionOverrides] = useState({});
  const [selectedRemoteAccess, setSelectedRemoteAccess] = useState(false);
  const [forcePasswordReset, setForcePasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatedEmail, setUpdatedEmail] = useState('');
  const [newUser, setNewUser] = useState(emptyUser);
  const [newService, setNewService] = useState(emptyService);
  const [newDiscount, setNewDiscount] = useState(emptyDiscount);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [editingGroupId, setEditingGroupId] = useState('');
  const [accessGroupForm, setAccessGroupForm] = useState(emptyAccessGroup);
  const [editingAccessGroupId, setEditingAccessGroupId] = useState('');
  const [specialRuleForm, setSpecialRuleForm] = useState(emptySpecialRule);
  const [editingSpecialRuleKey, setEditingSpecialRuleKey] = useState('');
  const [preferences, setPreferences] = useState(emptyPreferences);
  const [ipForm, setIpForm] = useState(emptyIpForm);
  const [preferencesStatus, setPreferencesStatus] = useState('');
  const [preferencesError, setPreferencesError] = useState('');
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [statusUserId, setStatusUserId] = useState(null);
  const [alertState, setAlertState] = useState({ open: false, message: '' });
  const [confirmState, setConfirmState] = useState({
    open: false,
    message: '',
    onConfirm: () => {},
  });
  const skipNextServiceBlurSaveRef = useRef(new Set());
  const skipNextDiscountBlurSaveRef = useRef(new Set());

  const selectedGroup = useMemo(
    () => groups.find((group) => groupKey(group) === String(selectedGroupId)),
    [groups, selectedGroupId]
  );
  const selectedAccessGroup = useMemo(
    () => accessGroups.find((group) => groupKey(group) === String(selectedAccessGroupId)),
    [accessGroups, selectedAccessGroupId]
  );
  const actionPermissionsByModule = useMemo(() => (
    (accessMetadata.actionPermissions || []).reduce((groups, permission) => {
      const moduleKey = permission.module || 'other';
      return {
        ...groups,
        [moduleKey]: [...(groups[moduleKey] || []), permission],
      };
    }, {})
  ), [accessMetadata.actionPermissions]);
  const permissionScopeOptions = useMemo(
    () => [...BUILT_IN_PERMISSION_SCOPES, ...(accessMetadata.specialRules || [])],
    [accessMetadata.specialRules]
  );
  const selectedSpecialRuleOperator = useMemo(
    () => (accessMetadata.specialRuleOperators || []).find((operator) => operator.key === specialRuleForm.operator),
    [accessMetadata.specialRuleOperators, specialRuleForm.operator]
  );

  const showAlert = (message) => {
    setAlertState({ open: true, message });
  };

  const setDashboardStatus = (message, type = 'success', userId = null) => {
    setStatus(message);
    setStatusType(type);
    setStatusUserId(userId);
  };

  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: async () => {
        await onConfirm();
        setConfirmState({ open: false, message: '', onConfirm: () => {} });
      },
    });
  };

  const loadUsers = async () => {
    const res = await api.get('/users');
    setUsers(res.data || []);
  };

  const loadProviders = async () => {
    const res = await api.get('/providers');
    setProviders(res.data || []);
  };

  const loadServices = async () => {
    const res = await api.get('/services');
    setServices(res.data || []);
    return res.data || [];
  };

  const loadDiscounts = async () => {
    const res = await api.get('/discounts');
    setDiscounts(res.data || []);
    return res.data || [];
  };

  const loadGroups = async () => {
    const res = await api.get('/services/groups');
    setGroups(res.data || []);
    return res.data || [];
  };

  const loadAccessGroups = async () => {
    const res = await api.get('/access-groups');
    const groups = (res.data || []).map(group => ({...group, permissions: {
      ...group.permissions, reports: group.permissions?.reports ?? group.permissions?.customers ?? 'none',
    }}));
    setAccessGroups(groups);
    return groups;
  };

  const loadAccessMetadata = async () => {
    const res = await api.get('/access-groups/pages');
    setAccessMetadata({
      actionPermissions: res.data?.actionPermissions || [],
      specialRules: res.data?.specialRules || [],
      specialRuleFields: res.data?.specialRuleFields || [],
      specialRuleOperators: res.data?.specialRuleOperators || [],
    });
    return res.data || {};
  };

  const loadPreferences = async () => {
    const res = await api.get('/preferences');
    setPreferences({
      taxRate: res.data?.taxRate ?? '',
      ipWhitelist: res.data?.ipWhitelist || [],
    });
    return res.data || {};
  };

  const loadDashboardData = async () => {
    try {
      await Promise.all([loadUsers(), loadProviders(), loadServices(), loadDiscounts(), loadGroups(), loadAccessGroups(), loadAccessMetadata(), loadPreferences()]);
    } catch (error) {
      showAlert(error.response?.data?.error || error.message || 'Failed to load dashboard data.');
    }
  };

  useEffect(() => {
    document.title = "Admin Dashboard - Services Irene's Wigs.com";
    if (!user) return;
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!status || statusType !== 'success') return undefined;

    const timeoutId = window.setTimeout(() => {
      setStatus('');
      setStatusUserId(null);
    }, 20000);

    return () => window.clearTimeout(timeoutId);
  }, [status, statusType]);

  const handleUserClick = (nextUser) => {
    if (selectedUser?.id === nextUser.id) {
      setSelectedUser(null);
      setSelectedEntitlements([]);
      setNewPassword('');
      setConfirmPassword('');
      setUpdatedEmail('');
      setSelectedGroupId('');
      setSelectedAccessGroupId('');
      setSelectedActionOverrides({});
      setSelectedRemoteAccess(false);
      setForcePasswordReset(false);
      return;
    }

    setSelectedUser(nextUser);
    const entitlements = nextUser.entitlements
      ? nextUser.entitlements
        .split(',')
        .map((e) => Number(e.trim()))
        .filter((n) => !Number.isNaN(n))
      : [];
    setSelectedEntitlements(entitlements);
    setNewPassword('');
    setConfirmPassword('');
    setUpdatedEmail(nextUser.email || '');
    setSelectedGroupId(nextUser.technicianServiceGroupId || '');
    setSelectedAccessGroupId(nextUser.accessGroupId || '');
    setSelectedActionOverrides(nextUser.accessActionOverrides || {});
    setSelectedRemoteAccess(Boolean(nextUser.remoteAccess));
    setForcePasswordReset(Boolean(nextUser.forcePasswordReset || nextUser.mustChangePassword));
  };

  const handleEntitlementChange = (entId) => {
    const numId = Number(entId);
    setSelectedEntitlements((prev) => (
      prev.includes(numId) ? prev.filter((id) => id !== numId) : [...prev, numId]
    ));
  };

  const updateSelectedActionOverride = (permissionKey, value) => {
    const payloadValue = actionOverridePayloadValue(value);
    setSelectedActionOverrides((current) => {
      const next = { ...(current || {}) };
      if (payloadValue === undefined) {
        delete next[permissionKey];
      } else {
        next[permissionKey] = payloadValue;
      }
      return next;
    });
  };

  const handleAddUser = async (event) => {
    event.preventDefault();
    setDashboardStatus('');
    if (!validatePasswordPolicy(newUser.password)) {
      setDashboardStatus(passwordPolicyMessage, 'error');
      return;
    }
    try {
      const res = await api.post('/users', {
        name: newUser.name,
        email: newUser.email,
        passwordHash: newUser.password,
        role: newUser.role,
        entitlements: '',
        accessGroupId: newUser.accessGroupId || null,
        remoteAccess: newUser.remoteAccess === true,
        forcePasswordReset: newUser.forcePasswordReset === true,
      });
      setNewUser(emptyUser);
      setDashboardStatus('User added.', 'success', res.data?.id ?? null);
      await loadUsers();
    } catch (err) {
      setDashboardStatus(err.response?.data?.error || err.message || 'Failed to add user.', 'error');
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    if (newPassword && newPassword !== confirmPassword) {
      setDashboardStatus('Passwords do not match.', 'error', selectedUser.id);
      return;
    }
    if (newPassword && !validatePasswordPolicy(newPassword)) {
      setDashboardStatus(passwordPolicyMessage, 'error', selectedUser.id);
      return;
    }

    try {
      const updatePayload = {
        name: selectedUser.name,
        email: updatedEmail,
        role: selectedUser.role,
        entitlements: selectedEntitlements.join(','),
        technicianServiceGroupId: selectedUser.role === 'technician' ? selectedGroupId || null : null,
        accessGroupId: selectedAccessGroupId || null,
        accessActionOverrides: selectedActionOverrides,
        remoteAccess: selectedRemoteAccess,
        forcePasswordReset,
      };

      if (newPassword.trim()) {
        updatePayload.password_hash = newPassword;
      }

      await api.put(`/users/${selectedUser.id}`, updatePayload);
      setDashboardStatus('User updated.', 'success', selectedUser.id);
      setSelectedUser((current) => current ? {
        ...current,
        email: updatedEmail,
        technicianServiceGroupId: updatePayload.technicianServiceGroupId,
        technicianServices: selectedGroup?.services || [],
        accessGroupId: updatePayload.accessGroupId,
        accessGroup: selectedAccessGroup || null,
        accessPermissions: selectedAccessGroup?.permissions || {},
        accessActionOverrides: selectedActionOverrides,
        remoteAccess: selectedRemoteAccess,
        forcePasswordReset,
        mustChangePassword: forcePasswordReset || current.passwordExpired,
      } : current);
      setNewPassword('');
      setConfirmPassword('');
      await loadUsers();
    } catch (err) {
      setDashboardStatus(err.response?.data?.error || err.message || 'Failed to update user.', 'error', selectedUser.id);
    }
  };

  const handleDeleteUser = async (id) => {
    const userToDelete = users.find((u) => u.id === id);
    if (!userToDelete) return;

    if (userToDelete.role === 'admin') {
      const otherAdmins = users.filter((u) => u.role === 'admin' && u.id !== id);
      if (otherAdmins.length === 0) {
        showAlert('You must have at least one admin user. This is the last admin.');
        return;
      }
      if (user?.id === userToDelete.id) {
        showAlert('You cannot delete your own account.');
        return;
      }
    }

    askConfirm('Are you sure you want to delete this user?', async () => {
      await api.delete(`/users/${id}`);
      if (selectedUser?.id === id) {
        setSelectedUser(null);
        setSelectedEntitlements([]);
      }
      await loadUsers();
      setDashboardStatus('User deleted.');
    });
  };

  const handleAddService = async (event) => {
    event.preventDefault();
    try {
      await api.post('/services', {
        name: newService.name,
        description: newService.description,
        ...parseServicePriceInput(newService.defaultPrice),
        subServices: newService.subServices,
      });
      setNewService(emptyService);
      await Promise.all([loadServices(), loadGroups()]);
      setStatus('Service added.');
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to add service.');
    }
  };

  const handleDeleteService = async (service) => {
    askConfirm(`Delete service "${service.name}"?`, async () => {
      await api.delete(`/services/${service.serviceId || service.id}`);
      await Promise.all([loadServices(), loadGroups()]);
      setStatus('Service deleted.');
    });
  };

  const updateServiceInState = (serviceId, changes) => {
    setServices((current) => current.map((service) => (
      serviceKey(service) === String(serviceId) ? { ...service, ...changes } : service
    )));
    setGroups((current) => current.map((group) => ({
      ...group,
      services: (group.services || []).map((service) => (
        serviceKey(service) === String(serviceId) ? { ...service, ...changes } : service
      )),
    })));
  };

  const handleServiceFieldChange = (service, field, value) => {
    const key = serviceKey(service);
    if (field === 'description') {
      updateServiceInState(key, { description: value });
      return;
    }
    if (field === 'defaultPrice') {
      updateServiceInState(key, {
        defaultPrice: value.replace(/%/g, '').trim(),
        price: value.replace(/%/g, '').trim(),
        ...(String(value).includes('%') ? { defaultPriceType: 'percent', priceType: 'percent' } : {}),
      });
      return;
    }
    updateServiceInState(key, { subServices: value, sub_services: value });
  };

  const saveServiceField = async (service, field, value) => {
    const key = serviceKey(service);
    const currentService = services.find((row) => serviceKey(row) === key) || service;
    const description = field === 'description'
      ? value
      : currentService.description ?? '';
    const defaultPrice = field === 'defaultPrice'
      ? value
      : currentService.defaultPrice ?? currentService.price ?? 0;
    const defaultPriceType = field === 'defaultPrice'
      ? parseServicePriceInput(value, servicePriceType(currentService)).defaultPriceType
      : servicePriceType(currentService);
    const subServices = field === 'subServices'
      ? value
      : currentService.subServices ?? currentService.sub_services ?? 0;
    try {
      const response = await api.patch(`/services/${service.serviceId || service.id}`, {
        description,
        defaultPrice: field === 'defaultPrice'
          ? parseServicePriceInput(defaultPrice, servicePriceType(currentService)).defaultPrice
          : defaultPrice,
        defaultPriceType,
        subServices,
      });
      updateServiceInState(key, response.data);
      setStatus('Service updated.');
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to update service.');
      await Promise.all([loadServices(), loadGroups()]);
    }
  };

  const handleServiceFieldKeyDown = async (event, service, field) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const input = event.currentTarget;
    skipNextServiceBlurSaveRef.current.add(serviceInputKey(service, field));
    await saveServiceField(service, field, input.value);
    input.blur();
  };

  const handleServiceFieldBlur = async (event, service, field) => {
    const key = serviceInputKey(service, field);
    if (skipNextServiceBlurSaveRef.current.has(key)) {
      skipNextServiceBlurSaveRef.current.delete(key);
      return;
    }
    await saveServiceField(service, field, event.currentTarget.value);
  };

  const handleAddDiscount = async (event) => {
    event.preventDefault();
    try {
      await api.post('/discounts', {
        item: newDiscount.item,
        description: newDiscount.description,
        type: newDiscount.type,
        ...parseDiscountPriceInput(newDiscount.price),
      });
      setNewDiscount(emptyDiscount);
      await loadDiscounts();
      setStatus('Discount added.');
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to add discount.');
    }
  };

  const handleDeleteDiscount = async (discount) => {
    askConfirm(`Delete discount "${discount.item}"?`, async () => {
      await api.delete(`/discounts/${discount.discountId || discount.id}`);
      await loadDiscounts();
      setStatus('Discount deleted.');
    });
  };

  const updateDiscountInState = (discountId, changes) => {
    setDiscounts((current) => current.map((discount) => (
      discountKey(discount) === String(discountId) ? { ...discount, ...changes } : discount
    )));
  };

  const handleDiscountFieldChange = (discount, field, value) => {
    const key = discountKey(discount);
    if (field === 'price') {
      const parsedPrice = parseDiscountPriceInput(value, discountPriceType(discount));
      updateDiscountInState(key, {
        price: parsedPrice.price,
        priceType: parsedPrice.priceType,
        price_type: parsedPrice.priceType,
      });
      return;
    }
    updateDiscountInState(key, { [field]: value });
  };

  const saveDiscountField = async (discount, field, value) => {
    const key = discountKey(discount);
    const currentDiscount = discounts.find((row) => discountKey(row) === key) || discount;
    try {
      const response = await api.patch(`/discounts/${discount.discountId || discount.id}`, {
        description: field === 'description' ? value : currentDiscount.description ?? '',
        type: field === 'type' ? value : currentDiscount.type ?? 'Discount',
        ...(field === 'price'
          ? parseDiscountPriceInput(value, discountPriceType(currentDiscount))
          : {
            price: currentDiscount.price ?? 0,
            priceType: discountPriceType(currentDiscount),
          }),
      });
      updateDiscountInState(key, response.data);
      setStatus('Discount updated.');
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to update discount.');
      await loadDiscounts();
    }
  };

  const handleDiscountFieldKeyDown = async (event, discount, field) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const input = event.currentTarget;
    skipNextDiscountBlurSaveRef.current.add(discountInputKey(discount, field));
    await saveDiscountField(discount, field, input.value);
    input.blur();
  };

  const handleDiscountFieldBlur = async (event, discount, field) => {
    const key = discountInputKey(discount, field);
    if (skipNextDiscountBlurSaveRef.current.has(key)) {
      skipNextDiscountBlurSaveRef.current.delete(key);
      return;
    }
    await saveDiscountField(discount, field, event.currentTarget.value);
  };

  const resetGroupForm = () => {
    setEditingGroupId('');
    setGroupForm(emptyGroup);
  };

  const editGroup = (group) => {
    const serviceSubServices = {};
    (group.services || []).forEach((service) => {
      serviceSubServices[serviceKey(service)] = selectedSubServicesForService(service).map(String);
    });
    setEditingGroupId(groupKey(group));
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      serviceIds: (group.services || []).map(serviceKey),
      serviceSubServices,
    });
  };

  const toggleGroupService = (service) => {
    const key = serviceKey(service);
    setGroupForm((current) => ({
      ...current,
      serviceIds: current.serviceIds.includes(key)
        ? current.serviceIds.filter((id) => id !== key)
        : [...current.serviceIds, key],
      serviceSubServices: current.serviceIds.includes(key)
        ? Object.fromEntries(Object.entries(current.serviceSubServices || {}).filter(([serviceId]) => serviceId !== key))
        : {
          ...(current.serviceSubServices || {}),
          [key]: serviceSubServiceNames(service),
        },
    }));
  };

  const toggleGroupSubService = (service, subServiceName) => {
    const key = serviceKey(service);
    const value = String(subServiceName);
    setGroupForm((current) => {
      const currentValues = current.serviceSubServices?.[key] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((name) => name !== value)
        : [...currentValues, value].sort((a, b) => a.localeCompare(b));
      return {
        ...current,
        serviceIds: current.serviceIds.includes(key) ? current.serviceIds : [...current.serviceIds, key],
        serviceSubServices: {
          ...(current.serviceSubServices || {}),
          [key]: nextValues,
        },
      };
    });
  };

  const handleSaveGroup = async (event) => {
    event.preventDefault();
    const payload = {
      name: groupForm.name,
      description: groupForm.description,
      serviceIds: groupForm.serviceIds,
      serviceAssignments: groupForm.serviceIds.map((serviceId) => ({
        serviceId,
        subServices: groupForm.serviceSubServices?.[serviceId] || [],
      })),
    };

    try {
      if (editingGroupId) {
        await api.put(`/services/groups/${editingGroupId}`, payload);
        setStatus('Service group updated.');
      } else {
        await api.post('/services/groups', payload);
        setStatus('Service group added.');
      }
      resetGroupForm();
      await Promise.all([loadGroups(), loadUsers()]);
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to save service group.');
    }
  };

  const handleDeleteGroup = async (group) => {
    askConfirm(`Delete service group "${group.name}"?`, async () => {
      await api.delete(`/services/groups/${group.groupId || group.id}`);
      if (editingGroupId === groupKey(group)) resetGroupForm();
      await Promise.all([loadGroups(), loadUsers()]);
      setStatus('Service group deleted.');
    });
  };

  const resetAccessGroupForm = () => {
    setEditingAccessGroupId('');
    setAccessGroupForm(emptyAccessGroup);
  };

  const resetSpecialRuleForm = () => {
    setEditingSpecialRuleKey('');
    setSpecialRuleForm(emptySpecialRule);
  };

  const editAccessGroup = (group) => {
    setEditingAccessGroupId(groupKey(group));
    setAccessGroupForm({
      name: group.name || '',
      description: group.description || '',
      permissions: {
        ...emptyAccessGroup.permissions,
        ...(group.permissions || {}),
      },
      actionPermissions: {
        ...(group.actionPermissions || {}),
      },
    });
  };

  const updateAccessPermission = (pageKey, authority) => {
    setAccessGroupForm((current) => ({
      ...current,
      permissions: {
        ...(current.permissions || {}),
        [pageKey]: authority,
      },
    }));
  };

  const updateActionPermission = (permissionKey, value) => {
    setAccessGroupForm((current) => ({
      ...current,
      actionPermissions: {
        ...(current.actionPermissions || {}),
        [permissionKey]: actionPermissionPayloadValue(value),
      },
    }));
  };

  const editSpecialRule = (rule) => {
    setEditingSpecialRuleKey(rule.scopeKey || rule.key || '');
    setSpecialRuleForm({
      scopeKey: rule.scopeKey || rule.key || '',
      label: rule.label || '',
      description: rule.description || '',
      fieldKey: rule.fieldKey || 'customer.type',
      operator: rule.operator || 'equals',
      comparisonValue: rule.comparisonValue || '',
    });
  };

  const handleSaveAccessGroup = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        name: accessGroupForm.name,
        description: accessGroupForm.description,
        permissions: accessGroupForm.permissions,
        actionPermissions: accessGroupForm.actionPermissions,
      };
      if (editingAccessGroupId) {
        await api.put(`/access-groups/${editingAccessGroupId}`, payload);
        setStatus('Access group updated.');
      } else {
        await api.post('/access-groups', payload);
        setStatus('Access group added.');
      }
      resetAccessGroupForm();
      await Promise.all([loadAccessGroups(), loadUsers()]);
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to save access group.');
    }
  };

  const handleSavePreferences = async (event) => {
    event.preventDefault();
    setPreferencesStatus('');
    setPreferencesError('');
    setPreferencesSaving(true);
    try {
      const taxRate = Number(preferences.taxRate || 0);
      const res = await api.put('/preferences', { taxRate });
      setPreferences((current) => ({
        ...current,
        taxRate: res.data?.taxRate ?? 0,
      }));
      setPreferencesStatus('Preferences saved.');
    } catch (err) {
      setPreferencesError(err.response?.data?.error || err.message || 'Failed to save preferences.');
    } finally {
      setPreferencesSaving(false);
    }
  };

  const handleSaveSpecialRule = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        scopeKey: specialRuleForm.scopeKey,
        label: specialRuleForm.label,
        description: specialRuleForm.description,
        fieldKey: specialRuleForm.fieldKey,
        operator: specialRuleForm.operator,
        comparisonValue: specialRuleForm.comparisonValue,
      };
      if (editingSpecialRuleKey) {
        await api.put(`/access-groups/special-rules/${editingSpecialRuleKey}`, payload);
        setStatus('Special rule updated.');
      } else {
        await api.post('/access-groups/special-rules', payload);
        setStatus('Special rule added.');
      }
      resetSpecialRuleForm();
      await Promise.all([loadAccessMetadata(), loadAccessGroups(), loadUsers()]);
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || 'Failed to save special rule.');
    }
  };

  const handleDeleteSpecialRule = (rule) => {
    askConfirm(`Delete special rule "${rule.label}"?`, async () => {
      await api.delete(`/access-groups/special-rules/${rule.scopeKey || rule.key}`);
      await Promise.all([loadAccessMetadata(), loadAccessGroups(), loadUsers()]);
      setStatus('Special rule deleted.');
    });
  };

  const handleAddWhitelistedIp = async (event) => {
    event.preventDefault();
    setPreferencesStatus('');
    setPreferencesError('');
    setPreferencesSaving(true);
    try {
      await api.post('/preferences/ip-whitelist', ipForm);
      setIpForm(emptyIpForm);
      const nextPreferences = await loadPreferences();
      setPreferencesStatus(`Added ${nextPreferences.ipWhitelist?.[0]?.ipAddress || 'IP address'} to the whitelist.`);
    } catch (err) {
      setPreferencesError(err.response?.data?.error || err.message || 'Failed to add IP address.');
    } finally {
      setPreferencesSaving(false);
    }
  };

  const handleRemoveWhitelistedIp = async (entry) => {
    askConfirm(`Remove "${entry.ipAddress}" from the office whitelist?`, async () => {
      setPreferencesStatus('');
      setPreferencesError('');
      try {
        await api.delete(`/preferences/ip-whitelist/${entry.id}`);
        await loadPreferences();
        setPreferencesStatus('IP address removed from the whitelist.');
      } catch (err) {
        setPreferencesError(err.response?.data?.error || err.message || 'Failed to remove IP address.');
      }
    });
  };

  const handleDeleteAccessGroup = async (group) => {
    askConfirm(`Delete access group "${group.name}"?`, async () => {
      await api.delete(`/access-groups/${group.groupId || group.id}`);
      if (editingAccessGroupId === groupKey(group)) resetAccessGroupForm();
      await Promise.all([loadAccessGroups(), loadUsers()]);
      setStatus('Access group deleted.');
    });
  };

  return (
    <>
      <TopNav />
      <div className="inventory-container admin-dashboard-page">
        <div className="admin-dashboard-header">
          <h2>Admin Dashboard</h2>
        </div>

        <div className="admin-dashboard-tabs" role="tablist" aria-label="Admin dashboard sections">
          <button type="button" className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
            Users
          </button>
          {canAccess('employees') && (
            <button type="button" className={activeTab === 'employees' ? 'active' : ''} onClick={() => setActiveTab('employees')}>
              Employees
            </button>
          )}
          {canAccess('vendors') && (
            <button type="button" className={activeTab === 'vendors' ? 'active' : ''} onClick={() => setActiveTab('vendors')}>
              Vendors
            </button>
          )}
          {canAccess('invoices') && (
            <button type="button" className={activeTab === 'invoices' ? 'active' : ''} onClick={() => setActiveTab('invoices')}>
              Invoices
            </button>
          )}
          <button type="button" className={activeTab === 'services' ? 'active' : ''} onClick={() => setActiveTab('services')}>
            Services
          </button>
          <button type="button" className={activeTab === 'discounts' ? 'active' : ''} onClick={() => setActiveTab('discounts')}>
            Discounts
          </button>
          <button type="button" className={activeTab === 'groups' ? 'active' : ''} onClick={() => setActiveTab('groups')}>
            Service Groups
          </button>
          <button type="button" className={activeTab === 'access' ? 'active' : ''} onClick={() => setActiveTab('access')}>
            Access Groups
          </button>
          <button type="button" className={activeTab === 'email' ? 'active' : ''} onClick={() => setActiveTab('email')}>
            Email Settings
          </button>
          <button type="button" className={activeTab === 'preferences' ? 'active' : ''} onClick={() => setActiveTab('preferences')}>
            Preferences
          </button>
        </div>

        {activeTab === 'users' && (
          <section>
            <form onSubmit={handleAddUser} className="inventory-form">
              <input
                type="text"
                placeholder="User Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="employee">Employee</option>
                <option value="employee_la">Employee Limited Access</option>
                <option value="technician">Technician</option>
                <option value="factory">Factory</option>
                <option value="admin">Admin</option>
                <option value="super">Super</option>
                <option value="payroll_manager">Payroll Manager</option>
                <option value="payroll_user">Payroll User</option>
              </select>
              <select value={newUser.accessGroupId || ''} onChange={(e) => setNewUser({ ...newUser, accessGroupId: e.target.value })}>
                <option value="">Access Group: role defaults</option>
                {accessGroups.map((group) => (
                  <option key={groupKey(group)} value={groupKey(group)}>
                    {group.name}
                  </option>
                ))}
              </select>
              <label className="admin-inline-checkbox">
                <input
                  type="checkbox"
                  checked={newUser.forcePasswordReset}
                  onChange={(e) => setNewUser({ ...newUser, forcePasswordReset: e.target.checked })}
                />
                <span className="admin-inline-checkbox-text">
                  Require password reset
                  <br />
                  on next login
                </span>
              </label>
              <label className="admin-inline-checkbox">
                <input
                  type="checkbox"
                  checked={newUser.remoteAccess}
                  onChange={(e) => setNewUser({ ...newUser, remoteAccess: e.target.checked })}
                />
                <span className="admin-inline-checkbox-text">
                  Remote Access
                </span>
              </label>
              <button type="submit">Add User</button>
            </form>

            {status && !statusUserId && <p className={`status-message ${statusType} admin-users-status`}>{status}</p>}

            <h3>Users</h3>
            <div className="card-list users">
              {users.map((u) => (
                <div key={u.id} className={`card ${selectedUser?.id === u.id ? 'selected' : ''}`}>
                  <div className="admin-user-card-header" onClick={() => handleUserClick(u)}>
                    <h2>{u.name} ({u.role})</h2>
                    {status && String(statusUserId) === String(u.id) && (
                      <span className={`status-message ${statusType} admin-users-status admin-card-status`}>{status}</span>
                    )}
                  </div>

                  {selectedUser?.id === u.id && (
                    <div className="user-detail-panel">
                      <h3>Manage Entitlements</h3>
                      {providers.map((provider) => (
                        <label key={provider.id} style={{ display: 'block', marginBottom: 6 }}>
                          <input
                            type="checkbox"
                            checked={selectedEntitlements.includes(provider.id)}
                            onChange={() => handleEntitlementChange(provider.id)}
                          />
                          {provider.name}
                        </label>
                      ))}

                      {selectedUser.role === 'technician' && (
                        <div className="admin-technician-group-control">
                          <h4>Service Group</h4>
                          <select value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
                            <option value="">No group assigned</option>
                            {groups.map((group) => (
                              <option key={groupKey(group)} value={groupKey(group)}>
                                {group.name}
                              </option>
                            ))}
                          </select>
                          <div className="admin-technician-services">
                            {selectedGroup?.services?.length ? (
                              selectedGroup.services.map((service) => (
                                <span key={serviceKey(service)}>{serviceAssignmentLabel(service)}</span>
                              ))
                            ) : (
                              <em>No services assigned.</em>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="admin-technician-group-control admin-access-group-control">
                        <h4>Access Group</h4>
                        <select value={selectedAccessGroupId} onChange={(e) => setSelectedAccessGroupId(e.target.value)}>
                          <option value="">Use role defaults</option>
                          {accessGroups.map((group) => (
                            <option key={groupKey(group)} value={groupKey(group)}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                        <div className="admin-technician-services">
                          {selectedAccessGroup ? (
                            ACCESS_PAGES
                              .filter((page) => selectedAccessGroup.permissions?.[page.key] && selectedAccessGroup.permissions[page.key] !== 'none')
                              .map((page) => (
                                <span key={page.key}>{page.label}: {selectedAccessGroup.permissions[page.key]}</span>
                              ))
                          ) : (
                              <em>Use Default role-based access</em>
                          )}
                        </div>
                      </div>

                      <div className="admin-technician-group-control admin-access-group-control">
                        <h4>Individual Action Overrides</h4>
                        <div className="admin-action-permissions">
                          {Object.entries(actionPermissionsByModule).map(([moduleKey, permissions]) => (
                            <div key={moduleKey} className="admin-action-module">
                              <h4>{moduleKey}</h4>
                              {permissions.map((permission) => {
                                const groupValue = selectedAccessGroup?.actionPermissions?.[permission.key];
                                const groupLabel = actionPermissionDisplay(groupValue, permissionScopeOptions);
                                return (
                                  <label key={permission.key} className="admin-action-permission-row">
                                    <span>{permission.action} <small>Group: {groupLabel}</small></span>
                                    <select
                                      value={actionOverrideValue(selectedActionOverrides?.[permission.key])}
                                      onChange={(e) => updateSelectedActionOverride(permission.key, e.target.value)}
                                    >
                                      <option value="inherit">Inherit from group</option>
                                      <option value="deny">Deny</option>
                                      <option value="allowed">Allowed</option>
                                      {permissionScopeOptions.map((rule) => (
                                        <option key={rule.scopeKey || rule.key} value={rule.scopeKey || rule.key}>
                                          {rule.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>

                      <label className="admin-inline-checkbox admin-remote-access-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedRemoteAccess}
                          onChange={(e) => setSelectedRemoteAccess(e.target.checked)}
                        />
                        <span className="admin-inline-checkbox-text">
                          Remote Access
                        </span>
                      </label>

                      <h4>Update Email</h4>
                      <input type="email" value={updatedEmail} onChange={(e) => setUpdatedEmail(e.target.value)} />

                      <label className="admin-inline-checkbox user-password-reset-checkbox">
                        <input
                          type="checkbox"
                          checked={forcePasswordReset}
                          onChange={(e) => setForcePasswordReset(e.target.checked)}
                        />
                        <span className="admin-inline-checkbox-text">
                          Require password reset
                          <br />
                          on next login
                        </span>
                      </label>

                      <h4>Update Password</h4>
                      <input
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <input
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />

                      <div className="user-controls">
                        <button type="button" onClick={handleSaveUser} className="button small yellow">
                          Save Changes
                        </button>
                        <button type="button" onClick={() => handleDeleteUser(selectedUser.id)} className="button small red">
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === 'employees' && canAccess('employees') && (
          <section>
            <AdminEmployeesPanel embedded />
          </section>
        )}

        {activeTab === 'vendors' && canAccess('vendors') && (
          <section>
            <VendorsPage embedded />
          </section>
        )}

        {activeTab === 'invoices' && canAccess('invoices') && (
          <section>
            <InvoicesPage embedded />
          </section>
        )}

        {activeTab === 'services' && (
          <section>
            <form onSubmit={handleAddService} className="inventory-form admin-service-form">
              <input
                type="text"
                placeholder="Service Name"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Description"
                value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
              />
              <input
                type="text"
                inputMode="decimal"
                placeholder="Default Price"
                value={newService.defaultPrice}
                onChange={(e) => setNewService({ ...newService, defaultPrice: e.target.value })}
              />
              <input
                type="text"
                placeholder="Subservices (comma separated)"
                value={newService.subServices}
                onChange={(e) => setNewService({ ...newService, subServices: e.target.value })}
              />
              <button type="submit">Add Service</button>
            </form>

            <div className="inventory-table-wrap">
              <table className="inventory-table admin-services-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Description</th>
                    <th>Price</th>
                    <th>Sub Services</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => (
                    <tr key={serviceKey(service)}>
                      <td>{service.name}</td>
                      <td>
                        <input
                          className="admin-service-inline-input description"
                          type="text"
                          value={service.description ?? ''}
                          onChange={(event) => handleServiceFieldChange(service, 'description', event.target.value)}
                          onKeyDown={(event) => handleServiceFieldKeyDown(event, service, 'description')}
                          onBlur={(event) => handleServiceFieldBlur(event, service, 'description')}
                        />
                      </td>
                      <td>
                        <span className={`admin-service-price-input ${servicePriceType(service) === 'percent' ? 'percent' : 'amount'}`}>
                          {servicePriceType(service) === 'amount' && <span>$</span>}
                          <input
                            className="admin-service-inline-input price"
                            type="text"
                            inputMode="decimal"
                            value={service.defaultPrice ?? service.price ?? ''}
                            onChange={(event) => handleServiceFieldChange(service, 'defaultPrice', event.target.value)}
                            onKeyDown={(event) => handleServiceFieldKeyDown(event, service, 'defaultPrice')}
                            onBlur={(event) => handleServiceFieldBlur(event, service, 'defaultPrice')}
                          />
                          {servicePriceType(service) === 'percent' && <span>%</span>}
                        </span>
                      </td>
                      <td>
                        <input
                          className="admin-service-inline-input"
                          type="text"
                          value={service.subServices ?? service.sub_services ?? 0}
                          onChange={(event) => handleServiceFieldChange(service, 'subServices', event.target.value)}
                          onKeyDown={(event) => handleServiceFieldKeyDown(event, service, 'subServices')}
                          onBlur={(event) => handleServiceFieldBlur(event, service, 'subServices')}
                        />
                      </td>
                      <td>
                        <button type="button" className="button small red" onClick={() => handleDeleteService(service)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {services.length === 0 && (
                    <tr>
                      <td colSpan="5">No services found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'discounts' && (
          <section>
            <form onSubmit={handleAddDiscount} className="inventory-form admin-service-form">
              <input
                type="text"
                placeholder="Item"
                value={newDiscount.item}
                onChange={(e) => setNewDiscount({ ...newDiscount, item: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Description"
                value={newDiscount.description}
                onChange={(e) => setNewDiscount({ ...newDiscount, description: e.target.value })}
              />
              <input
                type="text"
                placeholder="Type"
                value={newDiscount.type}
                onChange={(e) => setNewDiscount({ ...newDiscount, type: e.target.value })}
              />
              <input
                type="text"
                inputMode="decimal"
                placeholder="Price"
                value={newDiscount.price}
                onChange={(e) => setNewDiscount({ ...newDiscount, price: e.target.value })}
              />
              <button type="submit">Add Discount</button>
            </form>

            <div className="inventory-table-wrap">
              <table className="inventory-table admin-services-table admin-discounts-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((discount) => (
                    <tr key={discountKey(discount)}>
                      <td>{discount.item}</td>
                      <td>
                        <input
                          className="admin-service-inline-input description"
                          type="text"
                          value={discount.description ?? ''}
                          onChange={(event) => handleDiscountFieldChange(discount, 'description', event.target.value)}
                          onKeyDown={(event) => handleDiscountFieldKeyDown(event, discount, 'description')}
                          onBlur={(event) => handleDiscountFieldBlur(event, discount, 'description')}
                        />
                      </td>
                      <td>
                        <input
                          className="admin-service-inline-input type"
                          type="text"
                          value={discount.type ?? 'Discount'}
                          onChange={(event) => handleDiscountFieldChange(discount, 'type', event.target.value)}
                          onKeyDown={(event) => handleDiscountFieldKeyDown(event, discount, 'type')}
                          onBlur={(event) => handleDiscountFieldBlur(event, discount, 'type')}
                        />
                      </td>
                      <td>
                        <span className={`admin-service-price-input ${discountPriceType(discount) === 'percent' ? 'percent' : 'amount'}`}>
                          {discountPriceType(discount) === 'amount' && <span>$</span>}
                          <input
                            className="admin-service-inline-input price"
                            type="text"
                            inputMode="decimal"
                            value={discount.price ?? ''}
                            onChange={(event) => handleDiscountFieldChange(discount, 'price', event.target.value)}
                            onKeyDown={(event) => handleDiscountFieldKeyDown(event, discount, 'price')}
                            onBlur={(event) => handleDiscountFieldBlur(event, discount, 'price')}
                          />
                          {discountPriceType(discount) === 'percent' && <span>%</span>}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="button small red" onClick={() => handleDeleteDiscount(discount)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {discounts.length === 0 && (
                    <tr>
                      <td colSpan="5">No discounts found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'groups' && (
          <section className="admin-groups-layout">
            <form onSubmit={handleSaveGroup} className="inventory-card admin-group-editor">
              <h3>{editingGroupId ? 'Edit Service Group' : 'New Service Group'}</h3>
              <input
                type="text"
                placeholder="Group Name"
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              />
              <div className="admin-service-picker">
                {services.map((service) => {
                  const key = serviceKey(service);
                  const selected = groupForm.serviceIds.includes(key);
                  const subServiceNames = serviceSubServiceNames(service);
                  return (
                    <div key={key} className="admin-service-choice">
                      <label className="admin-service-option">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleGroupService(service)}
                        />
                        <span>{service.name}</span>
                        <strong>{servicePriceText(service)}</strong>
                      </label>
                      {selected && subServiceNames.length > 0 && (
                        <div className="admin-subservice-options">
                          {subServiceNames.map((name) => (
                            <label key={name}>
                              <input
                                type="checkbox"
                                checked={(groupForm.serviceSubServices?.[key] || []).includes(String(name))}
                                onChange={() => toggleGroupSubService(service, name)}
                              />
                              <span>{serviceSubServiceLabel(name)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="admin-group-actions">
                <button type="submit" className="button blue">
                  {editingGroupId ? 'Save Group' : 'Add Group'}
                </button>
                {editingGroupId && (
                  <button type="button" className="button admin-service-cancel" onClick={resetGroupForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="admin-group-list">
              {groups.map((group) => (
                <div className="card admin-group-card" key={groupKey(group)}>
                  <div>
                    <h3>{group.name}</h3>
                    {group.description && <p>{group.description}</p>}
                    <div className="admin-technician-services">
                      {(group.services || []).map((service) => (
                        <span key={serviceKey(service)}>{serviceAssignmentLabel(service)}</span>
                      ))}
                      {!group.services?.length && <em>No services assigned.</em>}
                    </div>
                  </div>
                  <div className="admin-group-actions">
                    <button type="button" className="button small yellow" onClick={() => editGroup(group)}>
                      Edit
                    </button>
                    <button type="button" className="button small red" onClick={() => handleDeleteGroup(group)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {groups.length === 0 && <div className="repair-empty-state">No service groups found.</div>}
            </div>
          </section>
        )}

        {activeTab === 'access' && (
          <section className="admin-groups-layout admin-access-groups-layout">
            <form onSubmit={handleSaveSpecialRule} className="inventory-card admin-group-editor admin-special-rule-editor">
              <h3>{editingSpecialRuleKey ? 'Edit Special Rule' : 'New Special Rule'}</h3>
              <input
                type="text"
                placeholder="Rule Key"
                value={specialRuleForm.scopeKey}
                onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, scopeKey: e.target.value })}
                disabled={Boolean(editingSpecialRuleKey)}
              />
              <input
                type="text"
                placeholder="Rule Label"
                value={specialRuleForm.label}
                onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, label: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={specialRuleForm.description}
                onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, description: e.target.value })}
              />
              <div className="admin-special-rule-grid">
                <label>
                  <span>Field</span>
                  <select
                    value={specialRuleForm.fieldKey}
                    onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, fieldKey: e.target.value })}
                  >
                    {(accessMetadata.specialRuleFields || []).map((field) => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Operator</span>
                  <select
                    value={specialRuleForm.operator}
                    onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, operator: e.target.value })}
                  >
                    {(accessMetadata.specialRuleOperators || []).map((operator) => (
                      <option key={operator.key} value={operator.key}>{operator.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Value</span>
                  <input
                    type="text"
                    placeholder={(accessMetadata.specialRuleFields || []).find((field) => field.key === specialRuleForm.fieldKey)?.valueHint || 'Value'}
                    value={specialRuleForm.comparisonValue}
                    onChange={(e) => setSpecialRuleForm({ ...specialRuleForm, comparisonValue: e.target.value })}
                    disabled={selectedSpecialRuleOperator?.requiresValue === false}
                    required={selectedSpecialRuleOperator?.requiresValue !== false}
                  />
                </label>
              </div>
              <div className="admin-group-actions">
                <button type="submit" className="button blue">
                  {editingSpecialRuleKey ? 'Save Special Rule' : 'Add Special Rule'}
                </button>
                {editingSpecialRuleKey && (
                  <button type="button" className="button admin-service-cancel" onClick={resetSpecialRuleForm}>
                    Cancel
                  </button>
                )}
              </div>
              <div className="admin-special-rule-list">
                {(accessMetadata.specialRules || []).map((rule) => (
                  <div key={rule.scopeKey || rule.key} className="admin-special-rule-row">
                    <div>
                      <strong>{rule.label}</strong>
                      <span>{rule.scopeKey || rule.key}</span>
                    </div>
                    <span>{rule.fieldKey} {rule.operator} {rule.comparisonValue}</span>
                    <div className="admin-group-actions">
                      <button type="button" className="button small yellow" onClick={() => editSpecialRule(rule)}>
                        Edit
                      </button>
                      {!rule.isSystem && (
                        <button type="button" className="button small red" onClick={() => handleDeleteSpecialRule(rule)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </form>

            <form onSubmit={handleSaveAccessGroup} className="inventory-card admin-group-editor admin-access-group-editor">
              <h3>{editingAccessGroupId ? 'Edit Access Group' : 'New Access Group'}</h3>
              <input
                type="text"
                placeholder="Group Name"
                value={accessGroupForm.name}
                onChange={(e) => setAccessGroupForm({ ...accessGroupForm, name: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={accessGroupForm.description}
                onChange={(e) => setAccessGroupForm({ ...accessGroupForm, description: e.target.value })}
              />
              <div className="admin-access-permissions">
                {ACCESS_PAGES.map((page) => (
                  <label key={page.key} className="admin-access-permission-row">
                    <span>{page.label}</span>
                    <select
                      value={accessGroupForm.permissions?.[page.key] || 'none'}
                      onChange={(e) => updateAccessPermission(page.key, e.target.value)}
                    >
                      {AUTHORITY_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <p>Reports access controls the Reports page. Report data still requires the corresponding Customers, Locations, Sales, or Payments permissions. Read, full, and elevated all allow read-only reporting.</p>
              <h3>Action Permissions</h3>
              <div className="admin-action-permissions">
                {Object.entries(actionPermissionsByModule).map(([moduleKey, permissions]) => (
                  <div key={moduleKey} className="admin-action-module">
                    <h4>{moduleKey}</h4>
                    {permissions.map((permission) => (
                      <label key={permission.key} className="admin-action-permission-row">
                        <span>{permission.action === 'crm' ? 'CRM' : permission.action}</span>
                        <select
                          value={actionPermissionValue(accessGroupForm.actionPermissions?.[permission.key])}
                          onChange={(e) => updateActionPermission(permission.key, e.target.value)}
                        >
                          <option value="none">Not allowed</option>
                          <option value="allowed">Allowed</option>
                          {permissionScopeOptions.map((rule) => (
                            <option key={rule.scopeKey || rule.key} value={rule.scopeKey || rule.key}>
                              {rule.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="admin-group-actions">
                <button type="submit" className="button blue">
                  {editingAccessGroupId ? 'Save Access Group' : 'Add Access Group'}
                </button>
                {editingAccessGroupId && (
                  <button type="button" className="button admin-service-cancel" onClick={resetAccessGroupForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="admin-group-list">
              {accessGroups.map((group) => (
                <div className="card admin-group-card admin-access-group-card" key={groupKey(group)}>
                  <div>
                    <h3>{group.name}</h3>
                    {group.description && <p>{group.description}</p>}
                    <div className="admin-technician-services">
                      {ACCESS_PAGES
                        .filter((page) => group.permissions?.[page.key] && group.permissions[page.key] !== 'none')
                        .map((page) => (
                          <span key={page.key}>{page.label}: {group.permissions[page.key]}</span>
                        ))}
                      {Object.entries(group.actionPermissions || {})
                        .filter(([, value]) => value === true || (value && value.granted !== false))
                        .map(([permissionKey, value]) => (
                          <span key={permissionKey}>
                            {permissionKey}: {actionPermissionDisplay(value, permissionScopeOptions)}
                          </span>
                        ))}
                      {!ACCESS_PAGES.some((page) => group.permissions?.[page.key] && group.permissions[page.key] !== 'none') && (
                        <em>No page access assigned.</em>
                      )}
                    </div>
                  </div>
                  <div className="admin-group-actions">
                    <button type="button" className="button small yellow" onClick={() => editAccessGroup(group)}>
                      Edit
                    </button>
                    <button type="button" className="button small red" onClick={() => handleDeleteAccessGroup(group)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {accessGroups.length === 0 && <div className="repair-empty-state">No access groups found.</div>}
            </div>
          </section>
        )}

        {activeTab === 'email' && (
          <section className="admin-email-settings">
            <EmailSettingsPanel updateTitle={false} />
          </section>
        )}

        {activeTab === 'preferences' && (
          <section className="admin-preferences-section">
            <form className="inventory-card admin-preferences-panel" onSubmit={handleSavePreferences}>
              <div className="admin-preferences-header">
                <h3>Preferences</h3>
                <button type="submit" className="button blue" disabled={preferencesSaving}>
                  {preferencesSaving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
              {preferencesStatus && <p className="status-message success admin-users-status">{preferencesStatus}</p>}
              {preferencesError && <p className="status-message error admin-users-status">{preferencesError}</p>}
              <label className="admin-tax-field">
                <span>Tax</span>
                <div className="admin-tax-input-wrap">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.0001"
                    value={preferences.taxRate}
                    onChange={(e) => setPreferences({ ...preferences, taxRate: e.target.value })}
                    required
                  />
                  <span>%</span>
                </div>
              </label>
            </form>
            <form className="inventory-card admin-preferences-panel admin-ip-whitelist-panel" onSubmit={handleAddWhitelistedIp}>
              <div className="admin-preferences-header">
                <h3>Office IP Whitelist</h3>
                <button type="submit" className="button blue" disabled={preferencesSaving}>
                  Add IP
                </button>
              </div>
              <div className="admin-ip-whitelist-form">
                <input
                  type="text"
                  placeholder="IP address or range"
                  value={ipForm.ipAddress}
                  onChange={(e) => setIpForm({ ...ipForm, ipAddress: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Label"
                  value={ipForm.label}
                  onChange={(e) => setIpForm({ ...ipForm, label: e.target.value })}
                />
              </div>
              <div className="admin-ip-whitelist-list">
                {(preferences.ipWhitelist || []).map((entry) => (
                  <div className="admin-ip-whitelist-row" key={entry.id}>
                    <div>
                      <strong>{entry.ipAddress}</strong>
                      {entry.label && <span>{entry.label}</span>}
                    </div>
                    <button
                      type="button"
                      className="button small red"
                      onClick={() => handleRemoveWhitelistedIp(entry)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {(preferences.ipWhitelist || []).length === 0 && (
                  <div className="repair-empty-state">No office IPs added. Remote access restrictions are inactive.</div>
                )}
              </div>
            </form>
          </section>
        )}
      </div>

      {alertState.open && (
        <AlertModal message={alertState.message} onClose={() => setAlertState({ open: false, message: '' })} />
      )}
      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState({ open: false, message: '', onConfirm: () => {} })}
        />
      )}
    </>
  );
};

export default AdminDashboard;
